"use client";

import { apiAddComment } from "@/apis/offers";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

interface RatingProps {
    offer_id: string;
    company_id: string;
}

const RateUs = ({ offer_id, company_id }: RatingProps) => {
    const [rating, setRating] = useState<number>(0);
    const [hovered, setHovered] = useState<number>(0);
    const [comment, setComment] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [hasRated, setHasRated] = useState(false);

    useEffect(() => {
        const lastRated = localStorage.getItem(`hasRated_${offer_id}`);
        if (lastRated && new Date().getTime() - parseInt(lastRated) < 86400000) {
            setHasRated(true);
        }
    }, [offer_id]);

    const handleRate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (hasRated) {
            toast.error("You have already rated, please try again later!", { autoClose: 2000 });
            return;
        }
        if (!rating && !comment.trim()) {
            toast.error("Please select a rating or add a comment.", { autoClose: 2000 });
            return;
        }
        setLoading(true);
        try {
            const response = await apiAddComment(offer_id, company_id, comment, rating.toString());
            if (response.status === "success" || (response as any).status === 200) {
                toast.success("Thank you for your feedback!", { autoClose: 2000 });
                setRating(0);
                setComment("");
                localStorage.setItem(`hasRated_${offer_id}`, new Date().getTime().toString());
                setHasRated(true);
            } else {
                toast.error("An error occurred. Please try again later!", { autoClose: 2000 });
            }
        } catch {
            toast.error("An error occurred while submitting feedback.", { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const activeRating = hovered || rating;

    const labels: Record<number, string> = {
        1: 'Poor',
        2: 'Fair',
        3: 'Good',
        4: 'Great',
        5: 'Excellent',
    };

    return (
        <form onSubmit={handleRate} autoComplete="off" className="flex flex-col gap-5">

            {/* Stars */}
            <div className="flex flex-col gap-2">
                <p className="text-[9px] font-black uppercase tracking-[0.3em] m-0"
                    style={{ color: 'rgba(122,252,32,0.5)' }}>
                    Your Rating
                </p>
                <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHovered(star)}
                            onMouseLeave={() => setHovered(0)}
                            className="focus:outline-none transition-transform duration-150 active:scale-90 hover:scale-110"
                            aria-label={`Rate ${star} stars`}
                        >
                            <svg
                                className="w-6 h-6 transition-all duration-200"
                                viewBox="0 0 20 20"
                                fill={star <= activeRating ? '#7AFC20' : 'rgba(122,252,32,0.12)'}
                                style={star <= activeRating ? { filter: 'drop-shadow(0 0 4px rgba(122,252,32,0.6))' } : {}}
                            >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                        </button>
                    ))}

                    {/* Label */}
                    {activeRating > 0 && (
                        <span
                            className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 ml-1 transition-all duration-200"
                            style={{
                                background: 'rgba(122,252,32,0.08)',
                                border: '1px solid rgba(122,252,32,0.25)',
                                color: '#7AFC20',
                            }}
                        >
                            {labels[activeRating]}
                        </span>
                    )}
                </div>
            </div>

            {/* Comment */}
            <div className="flex flex-col gap-2">
                <label
                    htmlFor="rate-comment"
                    className="text-[9px] font-black uppercase tracking-[0.3em] m-0"
                    style={{ color: 'rgba(122,252,32,0.5)' }}
                >
                    Comment
                </label>
                <textarea
                    id="rate-comment"
                    placeholder="Share your experience..."
                    rows={3}
                    className="w-full px-4 py-3 text-sm resize-none outline-none transition-all duration-300"
                    style={{
                        background: 'rgba(122,252,32,0.04)',
                        border: '1px solid rgba(122,252,32,0.15)',
                        color: 'rgba(245,245,220,0.7)',
                        clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                    }}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </div>

            {/* Submit */}
            <div
                className="group/btn relative overflow-hidden transition-all duration-300 hover:-translate-y-0.5"
                style={{
                    border: '2px solid #7AFC20',
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                    boxShadow: '0 0 14px rgba(122,252,32,0.3)',
                    opacity: loading ? 0.6 : 1,
                }}
            >
                <span
                    className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-500 skew-x-12 pointer-events-none"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(122,252,32,0.12), transparent)' }}
                />
                <button
                    type="submit"
                    disabled={loading}
                    className="relative z-10 w-full flex items-center justify-center gap-2 py-3 text-[10px] font-black uppercase tracking-widest focus:outline-none disabled:cursor-not-allowed"
                    style={{ color: '#081C11', background: loading ? 'rgba(122,252,32,0.5)' : '#7AFC20' }}
                >
                    {loading ? (
                        <>
                            <span className="w-3.5 h-3.5 border-2 border-[#081C11] border-t-transparent rounded-full animate-spin" />
                            Submitting...
                        </>
                    ) : (
                        <>
                            Submit Feedback
                            <svg className="w-3 h-3 transition-transform duration-300 group-hover/btn:translate-x-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                            </svg>
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default RateUs;
